/**
 * Nova Teez — required custom-option feedback
 * ------------------------------------------------------------------
 * When a product uses Hulk Product Options, clicking "Add to cart"
 * without completing required option groups silently does nothing.
 * This script adds the missing feedback: it highlights the required
 * groups that are empty, shows a visible message next to the options,
 * and scrolls to it. It never blocks or alters the add-to-cart
 * flow itself — it only surfaces what is missing.
 */
(() => {
  'use strict';

  const BANNER_ID = 'novateez-required-options-banner';
  const HIGHLIGHT_CLASS = 'novateez-missing-option';
  const STYLES_ID = 'novateez-required-options-styles';

  const STYLES = `
    #${BANNER_ID} {
      background: #b3261e;
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.45;
      padding: 12px 18px;
      border-radius: 10px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      text-align: center;
      font-family: inherit;
      margin: 0 0 16px 0;
    }
    .${HIGHLIGHT_CLASS} {
      outline: 2px solid #e5484d;
      outline-offset: 4px;
      border-radius: 6px;
    }
  `;

  function injectStyles() {
    if (document.getElementById(STYLES_ID)) return;
    const style = document.createElement('style');
    style.id = STYLES_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function getBanner() {
    let banner = document.getElementById(BANNER_ID);
    if (!banner) {
      banner = document.createElement('div');
      banner.id = BANNER_ID;
      banner.setAttribute('role', 'alert');
      banner.style.display = 'none';
      document.body.appendChild(banner);
    }
    return banner;
  }

  function hulkContainers() {
    return document.querySelectorAll('[id^="hulkapps_options_"]');
  }

  function pageHasHulkOptions() {
    return hulkContainers().length > 0;
  }

  function isAddToCartButton(el) {
    if (!(el instanceof HTMLButtonElement)) return false;
    if (el.disabled) return false;
    const text = (el.textContent || '').trim().toLowerCase();
    const cls = String(el.className || '');
    const isAddToCartText = text === 'add to cart' || /^add to cart\s*\(\d+\)$/.test(text);
    const isAddToCartClass = /(^|\s)(add-to-cart-button|sticky-add-to-cart__button)(\s|$)/.test(cls);
    // Exclude dynamic checkout buttons (Shop Pay, PayPal, etc.)
    const isDynamicCheckout = /shopify-payment-button|dynamic-checkout/i.test(cls) || /buy with|pay now|check out with/i.test(text);
    return (isAddToCartText || isAddToCartClass) && !isDynamicCheckout;
  }

  function optionGroups() {
    const groups = [];
    hulkContainers().forEach((container) => {
      container.querySelectorAll('.hulkapps_option').forEach((group) => {
        if (String(group.className).split(/\s+/).includes('required')) groups.push(group);
      });
    });
    return groups;
  }

  function groupIsVisible(group) {
    return !!(group.offsetParent || group.getClientRects().length);
  }

  function groupIsSatisfied(group) {
    const inputs = group.querySelectorAll('input, select, textarea');
    for (const input of inputs) {
      if (input.type === 'radio' || input.type === 'checkbox') {
        if (input.checked) return true;
      } else if (input.type === 'file') {
        if (input.files && input.files.length > 0) return true;
      } else if (input instanceof HTMLSelectElement) {
        if (input.selectedIndex > 0 && input.value) return true;
      } else if (input instanceof HTMLTextAreaElement) {
        if (input.value && input.value.trim()) return true;
      } else if (input.type === 'text' || input.type === 'number' || input.type === 'email' || input.type === 'tel' || input.type === 'url') {
        // Only count visible text inputs, to ignore hidden helper fields
        if (input.offsetParent && input.value && input.value.trim()) return true;
      }
    }
    return false;
  }

  function groupName(group) {
    const nameEl = group.querySelector('.hulkapps_option_name');
    const name = (nameEl ? nameEl.textContent : '') || 'a required option';
    return name.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  }

  function missingGroups() {
    return optionGroups().filter((group) => groupIsVisible(group) && !groupIsSatisfied(group));
  }

  // Pending delayed scroll correction, if any. Only one may be in flight;
  // it is cancelled whenever feedback is re-rendered or cleared.
  let correctionTimer = null;

  function clearFeedback() {
    if (correctionTimer) {
      clearTimeout(correctionTimer);
      correctionTimer = null;
    }
    document.querySelectorAll('.' + HIGHLIGHT_CLASS).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    const banner = getBanner();
    banner.style.display = 'none';
    banner.textContent = '';
  }

  function scrollToElement(banner) {
    // Find the nearest scrollable ancestor (this theme may scroll inside
    // a .page-wrapper div rather than the window).
    let el = banner;
    let container = null;
    while (el && el !== document.documentElement) {
      const s = getComputedStyle(el);
      if (/auto|scroll|overlay/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 5) {
        container = el;
        break;
      }
      el = el.parentElement;
    }
    const scrollTopFor = (offset) => {
      const rect = banner.getBoundingClientRect();
      const cRect = container ? container.getBoundingClientRect() : { top: 0 };
      const current = container ? container.scrollTop : window.scrollY;
      return Math.max(0, current + (rect.top - cRect.top) - offset);
    };
    const apply = (top, instant) => {
      if (container) container.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
      else window.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
    };
    // The banner sits directly above the first missing choice, so scrolling
    // it to just below the header brings both the message and the choice
    // that needs an answer on screen.
    if (correctionTimer) clearTimeout(correctionTimer);
    apply(scrollTopFor(140), false);
    // The theme also scrolls on add-to-cart clicks, which can displace this
    // position. One delayed correction, cancelled by any re-render or by
    // feedback clearing, puts the banner back on screen.
    correctionTimer = setTimeout(() => {
      correctionTimer = null;
      const r = banner.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        apply(scrollTopFor(140), true);
      }
    }, 900);
  }

  function renderFeedback(missing, scroll) {
    // Any re-render cancels a pending scroll correction — the positions it
    // computed may no longer be relevant.
    if (correctionTimer) {
      clearTimeout(correctionTimer);
      correctionTimer = null;
    }
    injectStyles();
    const names = missing.map(groupName);
    const label = names.length === 1 ? 'option' : 'options';
    const message = 'Please complete the required ' + label + ': ' + names.join(', ') + '.';
    const banner = getBanner();
    // Keep the message directly above the first choice that still needs an
    // answer, so the explanation is always next to what needs fixing.
    const anchor = missing[0];
    if (anchor && anchor.parentNode && banner.nextElementSibling !== anchor) {
      anchor.parentNode.insertBefore(banner, anchor);
    }
    banner.textContent = message;
    banner.style.display = 'block';
    document.querySelectorAll('.' + HIGHLIGHT_CLASS).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    missing.forEach((group) => group.classList.add(HIGHLIGHT_CLASS));
    if (scroll) {
      // Scroll so the message and the first choice that still needs an
      // answer are both on screen.
      scrollToElement(banner);
    }
  }

  // Whether the customer has attempted an add-to-cart while options were
  // incomplete — feedback on changes is only shown after such an attempt,
  // so normal browsing never shows errors prematurely.
  let attempted = false;

  // Main click listener — runs on capture so feedback appears immediately
  document.addEventListener(
    'click',
    (event) => {
      if (!pageHasHulkOptions()) return;
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || !isAddToCartButton(button)) return;
      const missing = missingGroups();
      if (missing.length > 0) {
        attempted = true;
        renderFeedback(missing, true);
      } else {
        // Everything is valid — make sure no stale feedback or pending
        // scroll correction is left behind; the normal add-to-cart flow
        // (including Hulk's own logic) proceeds untouched.
        clearFeedback();
      }
    },
    { capture: true }
  );

  // Update or clear feedback as the customer changes options — only after
  // an unsuccessful add-to-cart attempt, never during normal browsing
  document.addEventListener(
    'change',
    (event) => {
      if (!attempted || !pageHasHulkOptions()) return;
      const target = event.target;
      if (target instanceof Element && target.closest('[id^="hulkapps_options_"]')) {
        const missing = missingGroups();
        if (missing.length === 0) {
          clearFeedback();
          attempted = false;
        } else {
          // Keep the message in sync with what is still missing (no re-scroll)
          renderFeedback(missing, false);
        }
      }
    },
    { capture: true }
  );
})();
